import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { ScoreGauge, MetricBar } from '../../components/article/DataViz';

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-xs uppercase tracking-[0.22em] text-stone-500 mb-4">
      {children}
    </div>
  );
}

export default function BlogTrafficQualityScorePage() {
  const { createLink } = useLanguageLink();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'How is a traffic quality score different from just blocking bots?',
      answer: 'Blocking bots is a binary decision: allow or reject. A traffic quality score is a scale. It answers not "bot or human?" but "how likely is this visitor to convert?". A real person on a residential proxy from another country who bounces in two seconds is not a bot — but not quality traffic either. A score lets you rank sources, not just filter out obvious bots.',
    },
    {
      question: 'Can you score traffic quality without slowing down the landing page?',
      answer: 'Yes. Axetra decides server-side in under 10 milliseconds — before the page starts rendering. The visitor notices no delay, and you get a score for every visit in real time. Landing page load speed is unchanged.',
    },
    {
      question: 'What data do you need to start scoring traffic quality?',
      answer: 'Nothing beyond a normal integration. Axetra analyzes signals already present in every HTTP request and browser session: network, device, behavior, engagement. No conversion pixels, CRM exports, or manual labeling required — the score is computed from the first visit and gets more accurate as it learns your vertical.',
    },
  ];

  const publishDate = '2026-07-28';

  const signals = [
    { idx: '01', name: 'Network & origin', tags: 'IP reputation · ASN · VPN/proxy · geo match', desc: 'Where the request truly comes from — residential, mobile, or data-center — and whether IP, language, and targeting agree.' },
    { idx: '02', name: 'Device & browser', tags: 'fingerprint · headless · automation', desc: 'The real device behind the request, exposing headless browsers and automation tools regardless of spoofed headers.' },
    { idx: '03', name: 'Behavior & timing', tags: 'mouse · scroll · click cadence', desc: 'Movement, scrolling, and the micro-variations in timing that separate a live human from a recorded script.' },
    { idx: '04', name: 'Engagement', tags: 'depth · dwell · return visits', desc: 'Scroll depth, time on page, and repeat visits — signals of genuine interest rather than an accidental landing.' },
  ];

  return (
    <>
      <SEO
        title="How to Score Ad Traffic Quality: A Guide to Traffic Quality Score (2026)"
        description="Clicks are not customers. Learn which signals make up traffic quality, how to build your own scoring system, and how to cut the sources that drain your budget."
        canonical="/blog/traffic-quality-score"
        keywords="traffic quality score, traffic quality, ad traffic quality, traffic filtering, bot traffic detection, invalid traffic, ad fraud protection, traffic analytics"
        ogImage="/images/og-blog-ml-bot-detection.jpg"
        ogType="article"
        publishedTime={publishDate}
        modifiedTime={publishDate}
      />
      <SchemaMarkup
        type="article"
        headline="How to Score Ad Traffic Quality: A Guide to Traffic Quality Score"
        author="Axetra Team"
        datePublished={publishDate}
        dateModified={publishDate}
        image="/images/og-blog-ml-bot-detection.jpg"
      />
      <SchemaMarkup type="faq" questions={faqs} />

      <article className="bg-[#faf8f4] text-stone-900">

        {/* Masthead */}
        <div className="border-b border-stone-300">
          <div className="container-marketing max-w-5xl py-4 flex items-center justify-between font-mono text-xs uppercase tracking-[0.22em] text-stone-500">
            <span>Traffic Quality Report</span>
            <span>Axetra · 2026</span>
          </div>
        </div>

        {/* Hero */}
        <header className="border-b border-stone-300">
          <div className="container-marketing max-w-5xl py-16 md:py-20">
            <div className="grid md:grid-cols-[1fr_auto] gap-12 md:gap-16 items-center">
              <div>
                <Eyebrow>Guide · 11 min · July 28, 2026</Eyebrow>
                <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6">
                  How to score the quality of your ad traffic
                </h1>
                <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-xl">
                  A high CTR and thousands of clicks mean nothing if visitors never convert. Here's what quality is made of, how to measure it per source, and how to stop paying for clicks that never pay you back.
                </p>
              </div>
              <div className="justify-self-center md:justify-self-end">
                <ScoreGauge score={82} size={220} />
              </div>
            </div>
          </div>
        </header>

        {/* Thesis line */}
        <div className="border-y border-stone-800 bg-stone-900 text-[#faf8f4]">
          <div className="container-marketing max-w-5xl py-14 md:py-20">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-stone-500">The thesis</span>
              <span className="h-px flex-1 bg-stone-700" />
            </div>
            <p className="font-display font-semibold text-4xl md:text-6xl leading-[1.08] tracking-tight max-w-3xl">
              Clicks lie.<br />
              <span className="text-emerald-400">Signals tell the truth.</span>
            </p>
          </div>
        </div>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* 01 — why clicks lie */}
          <section>
            <Eyebrow>01 / Why clicks lie</Eyebrow>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Picture two campaigns. Both drove <span className="font-mono font-semibold text-stone-900">10,000</span> clicks last month, both posted a CTR around <span className="font-mono font-semibold text-stone-900">4%</span>, both spent the same budget. On the dashboard they're twins. But the first produced <span className="font-mono font-semibold text-emerald-700">180</span> leads and the second produced <span className="font-mono font-semibold text-red-700">11</span>. The difference isn't the creative or the bids. It's traffic quality.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              A click is the cheapest and most misleading metric in performance marketing. Easy to get, easy to fake, nearly impossible to read out of context. A bot clicks like a human. A stray user who misfired clicks like a warm lead. In the report, they're identical.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Traffic quality scoring fixes this: instead of counting clicks, you assess <em>who</em> is behind them and how likely each visit is to turn into money.
            </p>

            <blockquote className="my-10 border-l-2 border-emerald-600 pl-6">
              <p className="font-display text-2xl md:text-3xl leading-snug text-stone-900">
                If a metric can be improved by adding bots, it's a vanity metric.
              </p>
            </blockquote>

            <p className="text-lg text-stone-700 leading-relaxed">
              Clicks, impressions, and CTR are trivial for bots to inflate. Traffic quality is not — bots don't convert and don't behave like real buyers under a load of hundreds of signals.
            </p>
          </section>

          {/* 02 — signals */}
          <section>
            <Eyebrow>02 / What a score is made of</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">Four independent signal groups</h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-8">
              No single signal is a verdict. A visitor on a VPN isn't necessarily a bot; a short session isn't always junk. But faking network, device, behavior, and engagement <em>at once</em>, so they converge into a natural human profile, is exponentially harder than faking any one. That combination is what a score measures.
            </p>

            <div className="border-t border-stone-300">
              {signals.map((s) => (
                <div key={s.idx} className="grid grid-cols-[auto_1fr] gap-5 py-6 border-b border-stone-300">
                  <div className="font-mono text-sm text-emerald-700 pt-1">{s.idx}</div>
                  <div>
                    <h3 className="font-display font-semibold text-xl mb-1">{s.name}</h3>
                    <p className="text-stone-600 leading-relaxed mb-2">{s.desc}</p>
                    <p className="font-mono text-xs uppercase tracking-wider text-stone-400">{s.tags}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 03 — scorecard */}
          <section>
            <Eyebrow>03 / The scorecard</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">How to read the signals</h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-8">
              A working cheat sheet. No single red flag is a verdict — but when a source collects them by the handful, turn it off.
            </p>

            <div className="overflow-x-auto border-t-2 border-stone-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-xs uppercase tracking-wider">
                    <th className="text-left py-3 pr-4 text-stone-500 font-medium">Signal</th>
                    <th className="text-left py-3 px-4 text-emerald-700 font-medium">Quality</th>
                    <th className="text-left py-3 pl-4 text-red-700 font-medium">Red flag</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Network', 'Residential / mobile IP', 'Data-center, public VPN'],
                    ['Geography', 'IP · language · targeting agree', 'IP and language mismatch'],
                    ['Device', 'Real browser, real device', 'Headless / automation'],
                    ['Timing', 'Natural variability', '"Too perfect" uniformity'],
                    ['Visit depth', 'Scroll, navigation, dwell', 'Bounce in 1–2s'],
                    ['Frequency', 'Unique distinct sessions', 'Bursts of identical sessions'],
                    ['Conversion', 'Consistently produces leads', 'Many clicks, zero actions'],
                  ].map(([sig, good, bad]) => (
                    <tr key={sig} className="border-b border-stone-200 align-top">
                      <td className="py-3 pr-4 font-mono text-stone-900">{sig}</td>
                      <td className="py-3 px-4 text-stone-700">{good}</td>
                      <td className="py-3 pl-4 text-stone-700">{bad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* 04 — case study: the visual centerpiece, full-bleed on paper */}
        <section className="border-y border-stone-300 bg-white">
          <div className="container-marketing max-w-5xl py-16">
            <div className="max-w-[680px] mx-auto md:mx-0">
              <Eyebrow>04 / The case</Eyebrow>
              <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">Same CTR, different outcome</h2>
              <p className="text-lg text-stone-700 leading-relaxed mb-12">
                Back to the two campaigns. By top-level metrics they're identical. Break their traffic down by signal and the difference is immediate.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Campaign A */}
              <div className="border border-stone-300 p-8 bg-[#faf8f4]">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.18em] text-stone-500">Campaign A</div>
                    <div className="font-display text-2xl font-bold text-emerald-700">180 leads</div>
                  </div>
                  <ScoreGauge score={82} size={132} />
                </div>
                <div className="space-y-4">
                  <MetricBar label="Residential / mobile" value={88} display="88%" tone="good" />
                  <MetricBar label="Visit depth" value={68} display="3.4 screens" tone="good" />
                  <MetricBar label="Sessions under 2s" value={6} display="6%" tone="good" />
                  <MetricBar label="Geo match" value={96} display="96%" tone="good" />
                </div>
              </div>

              {/* Campaign B */}
              <div className="border border-stone-300 p-8 bg-[#faf8f4]">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.18em] text-stone-500">Campaign B</div>
                    <div className="font-display text-2xl font-bold text-red-700">11 leads</div>
                  </div>
                  <ScoreGauge score={28} size={132} />
                </div>
                <div className="space-y-4">
                  <MetricBar label="Residential / mobile" value={39} display="39%" tone="bad" />
                  <MetricBar label="Visit depth" value={22} display="1.1 screens" tone="bad" />
                  <MetricBar label="Sessions under 2s" value={74} display="74%" tone="bad" />
                  <MetricBar label="Geo match" value={39} display="39%" tone="bad" />
                </div>
              </div>
            </div>

            <p className="text-lg text-stone-700 leading-relaxed mt-12 max-w-[680px] mx-auto md:mx-0">
              An identical CTR hid everything that mattered. Campaign B was paying for bot clicks and incentivized traffic from a single placement. Turning it off and shifting budget to sources scoring above <span className="font-mono font-semibold">60</span> lifted leads at the same spend — without changing a single ad or bid.
            </p>
          </div>
        </section>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* 05 — playbook */}
          <section>
            <Eyebrow>05 / The playbook</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">How to raise your traffic quality</h2>
            <div className="border-t border-stone-300">
              {[
                { n: '1', t: 'Score each visit, not the campaign', d: 'A campaign-level average hides the problem. Break quality down by source, placement, and ad — bad traffic concentrates in a few places.' },
                { n: '2', t: 'Cut low-scoring sources', d: 'Placements that consistently earn a low score get excluded. This isn\'t blocking people — it\'s refusing to pay for clicks that don\'t convert.' },
                { n: '3', t: 'Filter data centers, proxies, VPNs', d: 'Data-center traffic almost never becomes customers. Excluding it is the fastest way to raise your average score without losing real audience.' },
                { n: '4', t: 'Watch engagement, not the click', d: 'Visit depth and dwell time predict conversion better than CTR. Bias bids toward sources where people actually engage.' },
                { n: '5', t: 'Let the system learn your vertical', d: 'A "normal" visitor differs across e-commerce, lead-gen, and B2B. The longer scoring runs, the more precisely it separates quality in your niche.' },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-[auto_1fr] gap-5 py-6 border-b border-stone-300">
                  <div className="font-mono text-lg text-emerald-700">{s.n}</div>
                  <div>
                    <h3 className="font-display font-semibold text-xl mb-1">{s.t}</h3>
                    <p className="text-stone-600 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 06 — vanity vs quality */}
          <section>
            <Eyebrow>06 / The distinction</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Vanity vs. quality metrics</h2>
            <div className="overflow-x-auto border-t-2 border-stone-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-xs uppercase tracking-wider">
                    <th className="text-left py-3 pr-4 text-stone-500 font-medium">Question</th>
                    <th className="text-left py-3 px-4 text-stone-400 font-medium">Vanity</th>
                    <th className="text-left py-3 pl-4 text-emerald-700 font-medium">Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['How many arrived?', 'Impressions, clicks', 'Share of valid visits'],
                    ['How clickable is the ad?', 'CTR', 'CTR among quality traffic'],
                    ['What happens on page?', '—', 'Visit depth and dwell'],
                    ['Where is real value?', 'Clicks by source', 'Score by source'],
                    ['Can bots inflate it?', 'Yes, easily', 'No'],
                  ].map(([q, v, ql]) => (
                    <tr key={q} className="border-b border-stone-200 align-top">
                      <td className="py-3 pr-4 font-mono text-stone-900">{q}</td>
                      <td className="py-3 px-4 text-stone-500">{v}</td>
                      <td className="py-3 pl-4 text-stone-800 font-medium">{ql}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Stat band */}
        <section className="border-y border-stone-300 bg-stone-900 text-[#faf8f4]">
          <div className="container-marketing max-w-5xl py-14">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
              {[
                { stat: '<10ms', label: 'Score per visit', sub: 'No landing page slowdown' },
                { stat: '900+', label: 'Signals per visitor', sub: 'Network · device · behavior' },
                { stat: '0–100', label: 'One quality scale', sub: 'Compare any sources' },
              ].map((s) => (
                <div key={s.stat} className="text-center md:border-r md:border-stone-700 md:last:border-0">
                  <div className="font-mono font-bold text-5xl mb-2 tabular-nums text-emerald-400">{s.stat}</div>
                  <div className="font-display text-lg">{s.label}</div>
                  <div className="text-sm text-stone-400 mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* CTA */}
          <section>
            <div className="border border-stone-300 bg-white p-10 text-center">
              <h3 className="font-display font-bold text-2xl md:text-3xl mb-3">See the real quality of your traffic</h3>
              <p className="text-stone-600 mb-8 max-w-md mx-auto">
                Axetra assigns a Traffic Quality Score to every visit and shows which sources bring real people. Set up in 5 minutes, 14 days free.
              </p>
              <Link
                to={createLink('register')}
                className="inline-block bg-emerald-600 text-white font-semibold px-8 py-4 rounded-md hover:bg-emerald-700 transition-colors"
              >
                Start free
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Frequently asked questions</h2>
            <div className="border-t border-stone-300">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-stone-300">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between py-5 text-left"
                  >
                    <span className="font-display font-semibold text-lg text-stone-900 pr-4">{faq.question}</span>
                    <svg className={`w-5 h-5 text-stone-500 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-45' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  {openFaqIndex === index && (
                    <p className="pb-6 text-stone-600 leading-relaxed max-w-[600px]">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section>
            <Eyebrow>Conclusion</Eyebrow>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Clicks tell you someone tapped an ad. Traffic quality tells you whether it was worth your money. The gap between a growing campaign and a losing one is almost never visible in CTR — it hides in the signals a standard dashboard never shows.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Start scoring traffic by source, turn off what consistently fails, and shift budget to where real people convert. It takes no new creative and no bigger budget — just an honest look at what you're actually paying for.
            </p>
          </section>
        </div>
      </article>
    </>
  );
}
